/* eslint-disable max-classes-per-file */

import { CONCEPTS } from '@rover/rsdk/src/concepts';
import { type ConsentPurpose, AnalyticsEvent } from '@rover/types/src/Analytics';

import type { LastUsedLoginMethod } from './lastUsedLoginMethod';

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires once per sign-in screen that renders a "Last used" badge, carrying the
 * method the badge points at. Pairs with `login-method-click` to answer whether
 * a badged user goes on to use the badged method.
 *
 * &nbsp;
 */
export class LastUsedBadgeShown extends AnalyticsEvent {
  eventName = 'last-used-badge-shown';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.people_and_pets.authentication;

  constructor(lastUsedMethod: LastUsedLoginMethod) {
    const properties = { last_used_method: lastUsedMethod };

    // Also sent as observability tags, which is what puts them on the Datadog
    // metric this event mirrors to. Safe to forward whole: the only field is a
    // `LastUsedLoginMethod`, so the tag has four values.
    super(properties, properties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when a user picks a sign-in method on a screen that is showing a badge.
 * `is_last_used_method` is the duplicate-account signal: false means the user
 * is signing in a different way than they did last time on this device.
 *
 * Pairs with `last-used-badge-shown`, which supplies the impressions this is a
 * ratio of. A screen with no badge emits neither.
 *
 * &nbsp;
 */
export class LoginMethodClick extends AnalyticsEvent {
  eventName = 'login-method-click';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.people_and_pets.authentication;

  constructor(method: LastUsedLoginMethod, badgedMethod: LastUsedLoginMethod) {
    const properties = {
      method,
      last_used_method: badgedMethod,
      is_last_used_method: badgedMethod === method,
    };

    // See `LastUsedBadgeShown`: the tags are what make `is_last_used_method`
    // groupable in Datadog, which is the whole ratio. Bounded the same way.
    super(properties, properties);
  }
}

/**
 * &nbsp;
 *
 * # Description
 *
 * Fires when any login succeeds on a device in the badge gate that has a method
 * recorded, carrying the method that worked against the recorded one.
 * `from_badged_form` separates conversions of `last-used-badge-shown` impressions
 * from logins on other screens.
 *
 * Emitted from the client on the apps only. Web's copy of this event comes from
 * the server (`account.last_login_method`), which reads the badged method off
 * the `last_login_method` cookie — app API calls carry no cookies, so the
 * server emits nothing for an app login and there is no double counting.
 *
 * &nbsp;
 */
export class LoginMethodSuccess extends AnalyticsEvent {
  eventName = 'login-method-success';

  consentPurpose: ConsentPurpose = 'functional';

  concept = CONCEPTS.people_and_pets.authentication;

  constructor(
    method: LastUsedLoginMethod,
    badgedMethod: LastUsedLoginMethod,
    {
      isSignUp = false,
      fromBadgedForm = false,
    }: { isSignUp?: boolean; fromBadgedForm?: boolean } = {}
  ) {
    super({
      method,
      last_used_method: badgedMethod,
      is_last_used_method: badgedMethod === method,
      // A sign-up on a device that already has a recorded method is the
      // duplicate account this project exists to prevent, so it stays in the
      // stream rather than being filtered out here.
      is_sign_up: isSignUp,
      // Whether the screen this login started on was the one showing the badge.
      // Only these rows answer a `last-used-badge-shown`, so the funnel filters
      // on them; the rest still carry `is_last_used_method`, which measures
      // duplicate accounts wherever a badged device signs in. The server sends
      // the same five properties.
      from_badged_form: fromBadgedForm,
    });
  }
}
